import axios from 'axios';
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  fromPromise,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { NetworkError } from '@apollo/client/errors';
import { GraphQLFormattedError } from 'graphql';
import { ENV } from '../constants';
import { criarInterceptorDeRenovacao } from '../utils/criarInterceptorDeRenovacao';
import {
  tratarIndisponibilidade,
  MENSAGEM_SERVICO_INDISPONIVEL,
} from '../utils/tratarIndisponibilidade';
import refreshToken from '../utils/refreshToken';

const rest = axios.create({
  baseURL: ENV.REACT_APP_CMS_HOST || 'http://localhost:3333', // 'http://23.22.89.145:87',
  headers: {
    'x-tenant-id': ENV.REACT_APP_TENANT_ID,
  },
});

export const tratarErro401 = criarInterceptorDeRenovacao(rest);
rest.interceptors.response.use(response => response, tratarErro401);
rest.interceptors.response.use(response => response, tratarIndisponibilidade);

const httpLinkGraphQL = new HttpLink({
  uri: ENV.REACT_APP_CMS_GRAPHQL_HOST || 'http://localhost:3333/graphql', // 'http://23.22.89.145:87',
});

// Lê o token do localStorage a cada request (não só uma vez), pra nunca ficar
// desatualizado após login/refresh/logout. Necessário pro cms-api: Query continua
// pública sem token, mas Mutation agora exige sessão válida do yggdrasil.
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('@Internal:token');

  return {
    headers: {
      ...headers,
      'x-tenant-id': ENV.REACT_APP_TENANT_ID,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  };
});

interface OriginalErrorComMotivo {
  reason?: string;
  statusCode?: number;
}

export function precisaRenovarToken(
  graphQLErrors: ReadonlyArray<GraphQLFormattedError> | undefined,
): boolean {
  return !!graphQLErrors?.some(
    erro =>
      erro?.extensions?.code === 'UNAUTHENTICATED' &&
      (erro?.extensions?.originalError as OriginalErrorComMotivo | undefined)
        ?.reason === 'token',
  );
}

// 503 não está no mapa de exceções pré-definidas do @nestjs/apollo, então normalmente sai em
// extensions.status — mas também aceita extensions.originalError.statusCode como fallback,
// pra não depender só de um detalhe de implementação que pode mudar numa atualização da lib.
function ehIndisponibilidade(erro: GraphQLFormattedError): boolean {
  const original = erro?.extensions?.originalError as
    | OriginalErrorComMotivo
    | undefined;
  return (erro?.extensions?.status ?? original?.statusCode) === 503;
}

function trocarMensagensDeIndisponibilidade(
  graphQLErrors: ReadonlyArray<GraphQLFormattedError> | undefined,
): void {
  graphQLErrors?.forEach(erro => {
    if (ehIndisponibilidade(erro)) {
      Object.assign(erro, { message: MENSAGEM_SERVICO_INDISPONIVEL });
    }
  });
}

function trocarMensagemDeErroDeRede(
  networkError: NetworkError | undefined,
): void {
  if (
    networkError &&
    'statusCode' in networkError &&
    networkError.statusCode === 503
  ) {
    Object.assign(networkError, { message: MENSAGEM_SERVICO_INDISPONIVEL });
  }
}

// Equivalente do tratarErro401 acima, para Mutations GraphQL. `forward(operation)` reexecuta
// a partir do `authLink`, que relê o token (já renovado) do localStorage — por isso não é
// preciso setContext manual aqui. Se o refresh falhar, refreshToken() já limpa a sessão e
// redireciona; não reenviamos a mutation (ela iria sem token, mascarando a causa real).
export const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    trocarMensagensDeIndisponibilidade(graphQLErrors);
    trocarMensagemDeErroDeRede(networkError);

    if (!precisaRenovarToken(graphQLErrors)) {
      return undefined;
    }

    return fromPromise(refreshToken()).flatMap(() => forward(operation));
  },
);

const graphql = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLinkGraphQL]),
  cache: new InMemoryCache(),
});

export default { rest, graphql };
